"use client"

import React from "react"
import {
  loadWebbridge,
  WebbridgeProvider,
  useVariables,
} from "@tapcart/app-studio-sdk"
import { gidFromVariantId } from "lib/utils/formatting"
import logger from "lib/utils/logger"

const TRACK_REJECTION_GUARD_MARKER = "__tcTrackRejectionGuarded"

/**
 * Wraps `actions.trackAnalyticEvent` so its returned promise never surfaces an
 * unhandled rejection. Native shells respond to `analytics/track` with
 * `{}`/`undefined`, which fails the SDK's boolean successSchema and rejects
 * the promise with a raw yup ValidationError (Sentry NEXTJS-CONSUMER-PAGES-7JF).
 * The event itself is tracked before the response echo fails, so nothing is
 * lost by marking the rejection handled. The original promise is returned, so
 * callers that attach their own handlers see identical behavior.
 */
export const patchTrackAnalyticEventRejection = (actions: any): void => {
  const original = actions?.trackAnalyticEvent
  if (typeof original !== "function") return
  if ((original as any)[TRACK_REJECTION_GUARD_MARKER]) return
  const wrapped = function (this: unknown, ...args: any[]) {
    const result = original.apply(this, args)
    if (result && typeof result.then === "function") {
      result.catch(() => {})
    }
    return result
  }
  ;(wrapped as any)[TRACK_REJECTION_GUARD_MARKER] = true
  actions.trackAnalyticEvent = wrapped
}

const LEGACY_WEBBRIDGE_ACTIONS = {
  "cart/add": "addToCart",
  "discount/apply": "applyDiscount",
  "discount/remove/all": "removeDiscounts",
  "discount/remove": "removeDiscount",
  "giftcard/apply": "applyGiftCard",
  "cart/open": "openCart",
  "authentication/open": "authenticationOpen",
  "collection/open": "openCollection",
  "product/open": "openProduct",
  "app/toast": "showToast",
  "view/updated": "updateView",
  "customer/identity": "getCustomerIdentity",
  "view/rendered": "renderView",
  "screen/open": "openScreen",
  "view/scrollToBlockTop": "scrollToBlockTop",
  "view/scrollToBlockBottom": "scrollToBlockBottom",
  "cart/remove": "removeFromCart",
  "analytics/track": "trackAnalyticEvent",
  "cart/updateAttributes": "updateCartAttributes",
  "cart/updateNote": "updateCartNote",
  "vault/initialize": "vaultInitialize",
  "vault/get": "vaultGet",
  "vault/set": "vaultSet",
  "vault/remove": "vaultRemove",
  "vault/clear": "vaultClear",
  "vault/keys": "vaultKeys",
  "vault/observe": "vaultObserve",
  "wishlist/create": "wishlistCreate",
  "wishlists/get": "wishlistsGet",
  "wishlist/item/add": "wishlistItemAdd",
  "wishlist/item/remove": "wishlistItemRemove",
  "enable/push": "enablePush",
}

// Initialize window.Tapcart as early as possible on the client so that older
// React Native app versions which inject window.Tapcart.mobile.load() before
// waiting for the webbridge/ready signal don't see an undefined window.Tapcart.
if (typeof window !== "undefined") {
  loadWebbridge()
}

const WebBridgeContextProvider = ({
  children,
}: {
  children?: React.ReactNode
}) => {
  const [webbridgeClient, setWebbridgeClient] = React.useState<any>(null)

  React.useEffect(() => {
    // loadWebbridge() is idempotent — returns the already-set window.Tapcart
    // if called again after the module-level call above.
    const client = loadWebbridge()

    // Patch actions BEFORE setting state so children only ever see the patched
    // version on their first render — avoids an extra re-render cycle.
    // Applied unconditionally (not behind the block-analytics flag): every
    // fleet version that responds non-boolean to analytics/track would
    // otherwise produce unhandled rejections (Sentry NEXTJS-CONSUMER-PAGES-7JF).
    patchTrackAnalyticEventRejection(client?.actions)
    if (typeof window !== "undefined") {
      const globalTapcart = (window as any).Tapcart
      if (globalTapcart?.actions && globalTapcart.actions !== client?.actions) {
        patchTrackAnalyticEventRejection(globalTapcart.actions)
      }
      queueMicrotask(() => {
        const late = (window as any).Tapcart
        if (late?.actions && late.actions !== client?.actions) {
          patchTrackAnalyticEventRejection(late.actions)
        }
      })
    }

    setWebbridgeClient(client)

    // Register legacy listeners
    if (!window.hasRegisteredLegacyWebbridgeListeners) {
      window.addEventListener("message", (eventData) => {
        const { data, source } = eventData
        const { type, message } = data
        if (source === window || type !== "tapcart-sdk") {
          return
        }
        if (typeof message !== "string" || !message) {
          return
        }
        let messageObj: any
        try {
          messageObj = JSON.parse(message)
        } catch (err) {
          logger.warn({
            message: "webbridge message parse failed",
            raw: message,
          })
          return
        }
        const actionName = LEGACY_WEBBRIDGE_ACTIONS[messageObj?.name]

        const id = messageObj?.id
        const actionData = messageObj?.data

        if (
          actionName === "openProduct" &&
          actionData?.isRelatedProduct &&
          (actionData?.variantId || actionData?.productId)
        ) {
          const url = new URL(window.location.href)

          if (actionData?.productId) {
            url.searchParams.set("productId", actionData.productId)
          }

          if (actionData?.variantId) {
            url.searchParams.set(
              "variantId",
              gidFromVariantId(actionData.variantId)
            )
          }

          window.history.pushState({}, "", url.toString())
          return
        }

        if (actionName && client.actions.hasOwnProperty(actionName)) {
          const onSuccess = (payload) => {
            window.postMessage(
              {
                type: "legacy-sdk-action-success",
                id,
                payload,
              },
              "*"
            )
          }
          const onError = (payload) => {
            window.postMessage(
              {
                type: "legacy-sdk-action-error",
                id,
                payload,
              },
              "*"
            )
          }

          client.actions[actionName](actionData, { onSuccess, onError })
        }
      })

      window.hasRegisteredLegacyWebbridgeListeners = true
    }
  }, [])

  if (!webbridgeClient) {
    return null
  }

  return (
    <WebbridgeProvider webbridgeClient={webbridgeClient}>
      <WebbridgeReadyGate>{children}</WebbridgeReadyGate>
    </WebbridgeProvider>
  )
}

const NATIVE_INJECTION_TIMEOUT_MS = 75

/**
 * Gates children until native variables are injected via mobile.load().
 *
 * In a React Native WebView, waits for isInitialized (typically ~4ms).
 * In standalone web, renders immediately since native injection never occurs.
 * Safety timeout prevents an indefinite blank screen if native injection fails.
 */
const WebbridgeReadyGate = ({ children }: { children: React.ReactNode }) => {
  const variables = useVariables()
  const isInitialized = variables?.isInitialized ?? false
  const isInWebView = "ReactNativeWebView" in window
  const [timedOut, setTimedOut] = React.useState(false)

  React.useEffect(() => {
    if (!isInWebView || isInitialized) return
    const timeout = setTimeout(
      () => setTimedOut(true),
      NATIVE_INJECTION_TIMEOUT_MS
    )
    return () => clearTimeout(timeout)
  }, [isInWebView, isInitialized])

  if (!isInWebView || isInitialized || timedOut) return <>{children}</>

  return null
}

export default WebBridgeContextProvider
